import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  Stack,
} from "@mui/material";
import {
  Videocam,
  ChevronRight,
  FiberManualRecord,
  AccessTime,
  Check,
} from "@mui/icons-material";
import { AxiosService } from "../../../services/axios/axiosService";
import { useParams, useNavigate,useLocation } from "react-router-dom";
import Timer from "./Timer";
// Progress Component
const ProgressTracker = React.memo(({ currentQuestion, totalQuestions }) => (
  <Paper
    sx={{
      p: 2,
      borderRadius: 2,
      border: "1px solid rgba(148, 163, 184, 0.4)",
    }}
  >
    <Typography
      variant="body1"
      fontWeight={600}
      mb={2}
    >
      Questions:
    </Typography>

    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        mt: 0.5,
      }}
    >
      {Array.from({ length: totalQuestions }, (_, i) => {
        const num = i + 1;
        const isActive = num === currentQuestion;
        const isCompleted = num < currentQuestion;

        let borderColor = "#d4d7db";
        let bgColor = "#ffffff";
        let textColor = "#4b5563";
        let IconComponent = null;

        if (isCompleted) {
          borderColor = "#22c55e";
          bgColor = "#ffffff";
          textColor = "#166534";
          IconComponent = Check;
        } else if (isActive) {
          borderColor = "#3b82f6";
          bgColor = "#ffffff";
          textColor = "#1d4ed8";
        }

        return (
          <Box
            key={num}
            sx={{
              position: "relative",
              width: 45,
              height: 45,
              borderRadius: 6,
              border: `2px solid ${borderColor}`,
              backgroundColor: bgColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 15,
              fontWeight: 600,
              color: textColor,
              boxSizing: "border-box",
              ...(isActive && {
                animation: "questionPulse 1.6s ease-in-out infinite",
              }),
              "@keyframes questionPulse": {
                "0%": {
                  boxShadow: "0 0 0 0 rgba(59,130,246,0.4)",
                  transform: "translateY(0)",
                },
                "50%": {
                  boxShadow: "0 0 0 6px rgba(59,130,246,0)",
                  transform: "translateY(-1px)",
                },
                "100%": {
                  boxShadow: "0 0 0 0 rgba(59,130,246,0)",
                  transform: "translateY(0)",
                },
              },
            }}
          >
            {IconComponent && (
              <Box
              
                sx={{
                  position: "absolute",
                  top: -6,
                  right: -6,
                  width: 16,
                  height: 16,
                  borderRadius: 5,
                  border: `1px solid ${borderColor}`,
                  backgroundColor: isCompleted ? "#22c55e" : "#ef4444",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: `0 0 0 2px ${borderColor}`,
                }}
              >
                <IconComponent
                  sx={{
                    fontSize: 12,
                    color: "#f9fafb",
                  }}
                />
              </Box>
            )}
            {num}
          </Box>
        );
      })}
    </Box>
  </Paper>
));

// Current Question Component
const CurrentQuestion = React.memo(
  ({ question, questionNumber, onNext, isSubmit, isUploading, isNextEnabled }) => {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          //bgcolor: "#f8fafc",
          border: "1px solid rgba(170, 168, 160, 0.3)",

          borderRadius: 2,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} mb={1.5}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontWeight: "bold",
            }}
          >
            {questionNumber}
          </Box>
          <Typography variant="body1"fontWeight={600} color="text.secondary" >
             Question
          </Typography>
        </Stack>

        <Typography variant="h6"  fontWeight={500} mb={2}>
          {question}
        </Typography>

        {/* <Typography variant="body2" color="rgba(255, 255, 255, 0.5)" mb={3}>
          Take your time to provide a thoughtful answer. Speak clearly and look
          at the camera. Please do not switch tabs or refresh the page while recording.
        </Typography> */}

        <Button
          variant="contained"
          size="medium"
          endIcon={isSubmit ? null : <ChevronRight />}
          onClick={onNext}
          disabled={!isNextEnabled}
          sx={{
            textTransform: "none",
            borderRadius: 1.5,
            mb: 2,
            mt: "auto",
            alignSelf: "flex-start",
            px: 2,
            "&:disabled": {
              bgcolor: "rgba(100, 102, 207, 0.5)",
            },
          }}
        >
          {isSubmit ? "Submit Interview" : "Next Question"}
        </Button>
      </Paper>
    );
  },
);

// Video Preview Component
const VideoPreview = React.memo(
  ({
    isLive,
    mediaRecorderRef,
    audioRecorderRef,
    mediaStreamRef,
    audioStreamRef,
    onRecordingStart,
    timeRemaining,
  }) => {
    const videoRef = useRef(null);
    const [cameraError, setCameraError] = useState(null);
    useEffect(() => {
      let videoStream = null;
      let audioStream = null;

      const initCamera = async () => {
        try {
          // Get video and audio stream
          videoStream = await navigator.mediaDevices.getUserMedia({
            video: {
              // Lower resolution + frame rate to reduce CPU usage
              width: { ideal: 640 },
              height: { ideal: 360 },
              frameRate: { ideal: 24, max: 30 },
            },
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
            },
          });

          // Get separate audio-only stream for transcript
          audioStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
            },
          });

          if (videoRef.current) {
            videoRef.current.srcObject = videoStream;
            mediaStreamRef.current = videoStream;
            audioStreamRef.current = audioStream;
          }

          // Notify parent that camera is ready
          onRecordingStart(videoStream, audioStream);
        } catch (err) {
          console.error("Error accessing camera:", err);
          setCameraError(err.message);
        }
      };

      initCamera();

      return () => {
        if (videoStream) {
          videoStream.getTracks().forEach((track) => track.stop());
        }
        if (audioStream) {
          audioStream.getTracks().forEach((track) => track.stop());
        }
      };
    }, [mediaStreamRef, audioStreamRef, onRecordingStart]);

    return (
      <Paper
        elevation={0}
        sx={{
          position: "relative",
          height: "100%",
          minHeight: 400,
          background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
          border: "1px solid rgba(255, 255, 255, 0.53)",
          borderRadius: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {isLive && (
          <Chip
            icon={
              <FiberManualRecord
                sx={{ fontSize: 12, animation: "pulse 2s infinite" }}
              />
            }
            label="Recording"
            size="small"
            sx={{
              position: "absolute",
              top: 24,
              left: 24,
              bgcolor: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(10px)",
              color: "white",
              zIndex: 10,
              "& .MuiChip-icon": {
                color: "#ef4444",
              },
            }}
          />
        )}

        {timeRemaining > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 24,
              right: 24,
              px: 1.2,
              py: 0.5,
              borderRadius: 999,
              bgcolor:
                timeRemaining <= 30
                  ? "rgba(127,29,29,0.95)"
                  : "rgba(15,23,42,0.8)",
              border:
                timeRemaining <= 30
                  ? "1px solid rgba(248,113,113,0.9)"
                  : "1px solid rgba(148,163,184,0.6)",
              color: "white",
              fontSize: 15,
              fontWeight: 600,
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              boxShadow:
                timeRemaining <= 30
                  ? "0 0 0 1px rgba(248,113,113,0.6), 0 0 12px rgba(220,38,38,0.8)"
                  : "none",
              animation:
                timeRemaining <= 30
                  ? "timerPulse 1.5s ease-in-out infinite"
                  : "none",
            }}
          >
            <AccessTime
              sx={{
                fontSize: 16,
                color: timeRemaining <= 30 ? "#fecaca" : "#e5e7eb",
              }}
            />
            <Box component="span">
              {Math.floor(timeRemaining / 60)}:
              {(timeRemaining % 60).toString().padStart(2, "0")}
            </Box>
          </Box>
        )}

        {cameraError ? (
          <Stack alignItems="center" spacing={2}>
            <Videocam
              sx={{ fontSize: 96, color: "rgba(255, 255, 255, 0.2)" }}
            />
            <Typography variant="h6" color="rgba(255, 255, 255, 0.4)">
              Camera access denied
            </Typography>
            <Typography variant="body2" color="rgba(255, 255, 255, 0.3)">
              {cameraError}
            </Typography>
          </Stack>
        ) : (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              //borderRadius: "12px",
            }}
          />
        )}

        <style>
          {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }

          @keyframes timerPulse {
            0%, 100% {
              transform: translateY(0);
              box-shadow: 0 0 0 0 rgba(248,113,113,0.6);
            }
            50% {
              transform: translateY(-1px);
              box-shadow: 0 0 0 6px rgba(248,113,113,0);
            }
          }
        `}
        </style>
      </Paper>
    );
  },
);

const Interview = () => {
  const { interviewId } = useLocation().state || {};
  const navigate = useNavigate();
  const [interviewData, setInterviewData] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [isLive, setIsLive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isTtsCompletedForCurrentQuestion, setIsTtsCompletedForCurrentQuestion] = useState(false);
  const [isErr, setIsErr] = useState();
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPreStartActive, setIsPreStartActive] = useState(false);
  const [preStartSeconds, setPreStartSeconds] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);

  const mediaRecorderRef = useRef(null);
  const audioRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioStreamRef = useRef(null);
  const videoChunksRef = useRef([]);
  const audioChunksRef = useRef([]);
  const audioPlayerRef = useRef(null);
  const timerRef = useRef(null);
  const hasFetchedInterviewRef = useRef(false);
  const hasPlayedFirstQuestionTtsRef = useRef(false);

  // Get actual recorded duration from the video blob metadata (in whole seconds)
  const getBlobDurationSeconds = useCallback((blob) => {
    return new Promise((resolve) => {
      if (!blob) {
        resolve(0);
        return;
      }
      try {
        const videoEl = document.createElement("video");
        const url = URL.createObjectURL(blob);
        videoEl.preload = "metadata";
        videoEl.onloadedmetadata = () => {
          const dur = videoEl.duration;
          URL.revokeObjectURL(url);
          if (Number.isFinite(dur) && !Number.isNaN(dur)) {
            resolve(Math.max(0, Math.floor(dur)));
          } else {
            resolve(0);
          }
        };
        videoEl.onerror = () => {
          URL.revokeObjectURL(url);
          resolve(0);
        };
        videoEl.src = url;
      } catch {
        resolve(0);
      }
    });
  }, []);

  // Play audio from blob or URL
  const playAudio = (audioData) => {
    try {
      let url;
      
      // If response is a blob
      if (audioData instanceof Blob) {
        url = URL.createObjectURL(audioData);
      } else if (typeof audioData === 'string') {
        // Check if it's a URL
        if (audioData.startsWith('http') || audioData.startsWith('blob:') || audioData.startsWith('data:')) {
          url = audioData;
        } else {
          // Try as base64 string
          try {
            const byteCharacters = atob(audioData);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'audio/mpeg' });
            url = URL.createObjectURL(blob);
          } catch {
            url = audioData;
          }
        }
      } else {
        url = audioData;
      }
            setAudioUrl(url);
      
      // Play audio with a slight delay to ensure src is set
      if (audioPlayerRef.current) {
        const audioEl = audioPlayerRef.current;
        audioEl.src = url;
        audioEl.load();
        // Start timer and enable Next button only when TTS finishes
        audioEl.onended = () => {
          if (timerRef.current && typeof timerRef.current.start === "function") {
            timerRef.current.start();
          }
          setIsTtsCompletedForCurrentQuestion(true);
        };
        const playPromise = audioEl.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log("Audio playing successfully");
            })
            .catch((error) => {
              console.error("Audio play error:", error);
              if (timerRef.current && typeof timerRef.current.start === "function") {
                timerRef.current.start();
              }
              setIsTtsCompletedForCurrentQuestion(true);
            });
        }
      }
    } catch (err) {
      console.error("Error playing audio:", err);
      if (timerRef.current && typeof timerRef.current.start === "function") {
        timerRef.current.start();
      }
      setIsTtsCompletedForCurrentQuestion(true);
    }
  };

  // Fetch audio script for a question; timer starts only when TTS ends (playAudio onended)
  const fetchAudioScript = async (questionText) => {
    try {
      if (!questionText || !questionText.trim()) {
        if (timerRef.current?.start) {
          timerRef.current.start();
        }
        setIsTtsCompletedForCurrentQuestion(true);
        return;
      }
      const body = { Text: questionText };
      const response = await AxiosService.post(
        `/api/Interview/text-to-speech`,
        body,
        { responseType: "blob" }
      );
      if (response) {
        playAudio(response);
      } else {
        if (timerRef.current?.start) {
          timerRef.current.start();
        }
        setIsTtsCompletedForCurrentQuestion(true);
      }
    } catch (err) {
      console.error("Error fetching audio script:", err);
      setIsErr(err.message);
      if (timerRef.current?.start) {
        timerRef.current.start();
      }
      setIsTtsCompletedForCurrentQuestion(true);
    }
  };
  const fetchInterviewData = async () => {
    try {
      const response = await AxiosService.post(
        `/api/Interview/${interviewId}/start`,
      );
      setInterviewData(response);
      // Start 5s pre-start countdown only after interview data is available
      setIsPreStartActive(true);
      setPreStartSeconds(5);
    } catch (err) {
      console.error(err);
      setIsErr(err.message);
      // toast.error(
      //   err.message || "Oops! Something went wrong while retrieving the data.",
      // );
    }
  };
  const completeInterview= async () => {
    try {
      const response = await AxiosService.post(
        `/api/Interview/${interviewId}/complete`,
      );
      //setInterviewData(response);
      const res = response;
      if(res.success === true){ 
        navigate(`/interview-completion/${interviewId}`);
      }
      
    } catch (err) {
      console.error(err);
      setIsErr(err.message);
      // toast.error(
      //   err.message || "Oops! Something went wrong while retrieving the data.",
      // );
    }
  };

  // Fetch interview data immediately on mount (or when interviewId changes)
  useEffect(() => {
    if (hasFetchedInterviewRef.current) return;
    hasFetchedInterviewRef.current = true;
    fetchInterviewData();
  }, [interviewId]);

  // Pre-interview countdown: wait 5 seconds before showing the interview UI
  useEffect(() => {
    if (!isPreStartActive) return;
    if (isErr) return;
    if (preStartSeconds <= 0) {
      setIsPreStartActive(false);
      return;
    }

    const intervalId = setInterval(() => {
      setPreStartSeconds((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          clearInterval(intervalId);
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [isPreStartActive, preStartSeconds, isErr]);

  // Trigger text-to-speech for the first question ONLY when the interview UI is visible
  useEffect(() => {
    if (isErr) return;
    if (isPreStartActive) return;
    if (!interviewData?.questions || interviewData.questions.length === 0) return;
    if (currentQuestion !== 1) return;
    if (hasPlayedFirstQuestionTtsRef.current) return;

    const firstQuestionText = interviewData.questions[0]?.questionText;
    hasPlayedFirstQuestionTtsRef.current = true;

    if (firstQuestionText) {
      fetchAudioScript(firstQuestionText);
    } else {
      setIsTtsCompletedForCurrentQuestion(true);
    }
  }, [isPreStartActive, isErr, interviewData, currentQuestion]);

  // Cleanup audio URL on unmount
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  // Start recording when camera is ready
  const handleRecordingStart = useCallback(
    (videoStream, audioStream) => {
      // Video recorder
      const videoRecorder = new MediaRecorder(videoStream, {
        mimeType: "video/webm;codecs=vp8,opus",
      });

      mediaRecorderRef.current = videoRecorder;
      videoChunksRef.current = [];

      videoRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          videoChunksRef.current.push(event.data);
        }
      };

      videoRecorder.onstop = () => {
        console.log("Video recording stopped");
      };

      // Audio recorder (separate for transcript)
      const audioRecorder = new MediaRecorder(audioStream, {
        mimeType: "audio/webm;codecs=opus",
      });

      audioRecorderRef.current = audioRecorder;
      audioChunksRef.current = [];

      audioRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      audioRecorder.onstop = () => {
        console.log("Audio recording stopped");
      };

      // Start both recordings (timer and questionStartTime set when TTS ends in playAudio onended)
      videoRecorder.start();
      audioRecorder.start();
      setIsLive(true);
      console.log("Recording started");
    },
    // Intentionally empty: stable reference so VideoPreview does not re-run initCamera on question change (avoids video refresh/flicker)
    [],
  );

  // Upload video and audio to API
  const uploadFiles = async (
    videoBlob,
    audioBlob,
    questionOrder,
    durationSeconds,
  ) => {
    const formData = new FormData();

    // Get the actual question ID from interview data using questionOrder
    const questionId = interviewData?.questions?.[questionOrder - 1]?.id;

    if (!questionId) {
      console.error("Question ID not found for order:", questionOrder);
      return false;
    }

    const videoFile = new File([videoBlob], `question_${questionId}.webm`, {
      type: "video/webm",
    });

    const audioFile = new File(
      [audioBlob],
      `question_${questionId}_audio.webm`,
      {
        type: "audio/webm",
      },
    );

    formData.append("videoFile", videoFile);
    formData.append("audioFile", audioFile);
    formData.append("interviewId", interviewId);
    formData.append("questionId", questionId);
    formData.append("durationSeconds", durationSeconds?.toString() ?? "0");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/interview/response`,
        {
          method: "POST",
          body: formData,
        },
      );

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const result = await response.json();
      console.log("Upload successful:", result);
      return true;
    } catch (error) {
      console.error("Error uploading files:", error);
      alert(
        `Failed to upload files for question ${questionId}. Please try again.`,
      );
      return false;
    }
  };

  // Handle next question
  const handleNextQuestion = useCallback(async () => {
    if (
      !mediaRecorderRef.current ||
      mediaRecorderRef.current.state !== "recording"
    ) {
      console.error("Recorder not available or not recording");
      return;
    }

    if (
      !audioRecorderRef.current ||
      audioRecorderRef.current.state !== "recording"
    ) {
      console.error("Audio recorder not available or not recording");
      return;
    }

    // Stop both recordings (keep isLive true to avoid flicker)
    mediaRecorderRef.current.stop();
    audioRecorderRef.current.stop();

    // Wait for the stop events to complete
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Create video and audio blobs from recorded chunks
    const videoBlob = new Blob(videoChunksRef.current, { type: "video/webm" });
    const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });

    // Calculate actual recorded duration for this question from the video metadata (in seconds).
    const durationSeconds = await getBlobDurationSeconds(videoBlob);

    // Upload in background – don't wait; allow user to proceed to next question immediately
    uploadFiles(
      videoBlob,
      audioBlob,
      currentQuestion,
      durationSeconds,
    ).catch((err) => {
      console.error("Background upload failed:", err);
      // Optionally show non-blocking toast: upload failed for question N
    });

    // Proceed immediately: last question → complete, else → next question with TTS; timer starts after TTS completion (playAudio onended)
    if (currentQuestion >= interviewData?.questions?.length) {
      completeInterview();
      return;
    }

    const nextQuestion = currentQuestion + 1;
    setCurrentQuestion(nextQuestion);
    setIsTtsCompletedForCurrentQuestion(false);

    // Reset chunks for next recording
    videoChunksRef.current = [];
    audioChunksRef.current = [];

    // Fetch audio script for the next question (await so TTS is ready)
    const nextQuestionText =
      interviewData?.questions?.[nextQuestion - 1]?.questionText;
    if (nextQuestionText) {
      await fetchAudioScript(nextQuestionText);
    } else {
      setIsTtsCompletedForCurrentQuestion(true);
    }

    // Start new recordings for next question
    if (mediaStreamRef.current && audioStreamRef.current) {
      const videoRecorder = new MediaRecorder(mediaStreamRef.current, {
        mimeType: "video/webm;codecs=vp8,opus",
      });

      mediaRecorderRef.current = videoRecorder;

      videoRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          videoChunksRef.current.push(event.data);
        }
      };

      videoRecorder.onstop = () => {
        console.log("Video recording stopped");
      };

      const audioRecorder = new MediaRecorder(audioStreamRef.current, {
        mimeType: "audio/webm;codecs=opus",
      });

      audioRecorderRef.current = audioRecorder;

      audioRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      audioRecorder.onstop = () => {
        console.log("Audio recording stopped");
      };

      videoRecorder.start();
      audioRecorder.start();
      setIsLive(true);
      console.log("New recordings started for question", nextQuestion);
    }
  }, [currentQuestion, interviewData, interviewId]);

  // Called when per-question timer hits 0 (same flow as Next: next question, TTS, timer starts after TTS completion)
  const handleTimeUp = useCallback(() => {
    handleNextQuestion();
  }, [handleNextQuestion]);

  const currentQuestionText = useMemo(() => {
    return interviewData?.questions?.[currentQuestion - 1]?.questionText || "";
  }, [currentQuestion, interviewData]);

  const currentQuestionDuration = useMemo(() => {
    const seconds =
      interviewData?.questions?.[currentQuestion - 1]
        ?.expectedDurationSeconds;
    return typeof seconds === "number" && seconds > 0 ? seconds : 0;
  }, [currentQuestion, interviewData]);

  useEffect(() => {
    const beforeUnload = (e) => {
      e.preventDefault();
      e.returnValue =
        "Are you sure you want to leave? Your progress might be lost.";
    };

    window.addEventListener("beforeunload", beforeUnload);

    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
    };
  }, []);

  return (
   <>
    {/* Hidden audio player - auto plays */}
    <audio ref={audioPlayerRef} autoPlay />
    
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", py: 4 }}>
      <Container maxWidth="xl">
       {
        isErr ? (
          <Box
            sx={{
              height: "70vh",
              display: "flex",
              alignItems: "center",   
              justifyContent: "center",
              textAlign: "center",
              px: 2,
            }}
          >
            <Typography variant="h5" color="#ffc107">
              {isErr}
            </Typography>
          </Box>
        ) : isPreStartActive ? (
          <Box
            sx={{
              height: "70vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              gap: 2,
            }}
          >
            <Typography variant="h4" fontWeight={600}>
              Your interview will start in {preStartSeconds} seconds
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Please get ready. Ensure your camera and microphone are enabled.
            </Typography>
          </Box>
        ) : interviewData?.questions ? (
          // <Paper
          //   elevation={0}
          //   sx={{
          //     p: 5,
          //     borderRadius: 3,
          //     border: "1px solid #e5e7eb",
          //     bgcolor: "#ffffff",
          //     boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
          //   }}
          // >
           <Grid container spacing={3}>
            {/* Question */}
          {/* <Grid item size={12}>
            <CurrentQuestion
              question={currentQuestionText}
              questionNumber={currentQuestion}
              onNext={handleNextQuestion}
              isSubmit={currentQuestion === interviewData?.questions?.length}
              isUploading={isUploading}
              isNextEnabled={isTtsCompletedForCurrentQuestion}
            />
          </Grid> */}
          
          {/* Video */}
          <Grid item size={{ xs: 12, md: 8 }}>
            <VideoPreview
              isLive={isLive}
              mediaRecorderRef={mediaRecorderRef}
              audioRecorderRef={audioRecorderRef}
              mediaStreamRef={mediaStreamRef}
              audioStreamRef={audioStreamRef}
              onRecordingStart={handleRecordingStart}
              timeRemaining={timeRemaining}
            />
          </Grid>

          {/* Right column */}
          <Grid item size={{ xs: 12, md: 4 }} sx={{ display: "flex" }}>
            <Stack
              spacing={3}
              sx={{
                flex: 1,
                alignItems: "stretch",
                justifyContent: "space-between",
              }}
            >
              {/* Top: timer + current question grouped together and stretched */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <Timer
                  ref={timerRef}
                  durationSeconds={currentQuestionDuration}
                  questionKey={currentQuestion}
                  onTimeUp={handleTimeUp}
                  startPaused={true}
                  onTick={setTimeRemaining}
                />
                
                  <CurrentQuestion
                    question={currentQuestionText}
                    questionNumber={currentQuestion}
                    onNext={handleNextQuestion}
                    isSubmit={
                      currentQuestion === interviewData?.questions?.length
                    }
                    isUploading={isUploading}
                    isNextEnabled={isTtsCompletedForCurrentQuestion}
                  />
                
              </Box>

              {/* Bottom: AI monitoring aligned with bottom of recorder */}
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  //bgcolor: "rgba(255, 193, 7, 0.1)",
                  border: "1px solid rgba(170, 168, 160, 0.3)",
                  borderRadius: 2,
                }}
              >
                <Typography variant="body2" color="#ef6e13">
                  <strong style={{ fontWeight: 600, fontSize: 16 }}>AI Monitoring Active :</strong> Your behavior,
                  responses, and engagement are being monitored. Please maintain
                  eye contact with the camera and avoid switching tabs or
                  typing.
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  mt={2}
                >
                  Take your time to provide a thoughtful answer. Speak clearly
                  and look at the camera. Please do not switch tabs or refresh
                  the page while recording.
                </Typography>
              </Paper>
            </Stack>
          </Grid>

          
           {/* Alert */}
          <Grid item size={12}>
            <ProgressTracker
              currentQuestion={currentQuestion}
              totalQuestions={interviewData?.questions?.length}
            />
          </Grid>
         
        </Grid>
        //</Paper>
        ) : (
          <Box
            sx={{
              height: "70vh",
              display: "flex",
              alignItems: "center",   
              justifyContent: "center",
              textAlign: "center",
              px: 2,
            }}
          >
            <Typography variant="h5" >
              {isErr ? `${isErr}` : "Loading..."}
            </Typography>
            {/* <Button onClick={fetchInterviewData} sx={{ ml: 2 }} variant="contained">
              Retry
            </Button> */}
          </Box>
        )
       }
      </Container>
    </Box>
   
   </>
  );
};

export default Interview;
